import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProductTranslation } from '@/hooks/useProductTranslation';
import type { ProductSummary } from '@/types/product';

interface ProductCardProps {
  product: ProductSummary;
  index: number;
}

const TAG_I18N_MAP: Record<string, string> = {
  '热销中': 'tag_hot_selling',
  '新品': 'tag_new',
  '瓦楞纸': 'tag_corrugated_paper',
  '限时特惠': 'tag_limited_offer',
  '爆款': 'tag_bestseller',
  '推荐': 'tag_recommended',
  '猫薄荷': 'tag_catnip',
  '剑麻': 'tag_sisal',
  '护角': 'tag_corner_protector',
  '爬架': 'tag_cat_tree',
  '环保': 'tag_eco_friendly',
};

const CATEGORY_I18N_MAP: Record<string, string> = {
  '瓦楞纸': 'category_corrugated',
  '剑麻': 'category_sisal',
  '综合': 'category_combo',
};

export default function ProductCard({ product, index }: ProductCardProps) {
  const { t } = useTranslation();
  const { getProductName, getProductDescription } = useProductTranslation();
  const displayName = getProductName(product as any);
  const displayDesc = getProductDescription(product as any);
  const discount = Math.round((1 - product.price / product.original_price) * 100);

  const translateTag = (tag: string): string => {
    const key = TAG_I18N_MAP[tag];
    return key ? t(key) : tag;
  };

  const translateCategory = (category: string): string => {
    const key = CATEGORY_I18N_MAP[category];
    return key ? t(key) : category;
  };

  return (
    <Link
      to={`/products/${product.id}`}
      className="group bg-white rounded-2xl overflow-hidden border border-[#f0ebe4] hover:border-[#C8714A]/40 transition-all duration-300 cursor-pointer flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative overflow-hidden bg-[#faf8f5] h-56">
        <img
          src={product.image}
          alt={displayName}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
        />

        {discount > 0 && (
          <div className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center rounded-full bg-[#C8714A] text-white text-xs font-bold">
            -{discount}%
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {product.tags?.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-0.5 rounded-full bg-white/90 text-[#C8714A] text-xs font-medium"
            >
              {translateTag(tag)}
            </span>
          ))}
        </div>

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full px-5 py-2 text-sm font-semibold text-[#1a1a1a] flex items-center gap-2">
            <i className="ri-eye-line text-sm"></i>
            {t('product_view_detail')}
          </div>
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-[#C8714A] font-medium bg-[#C8714A]/10 px-2 py-0.5 rounded-full">
            {translateCategory(product.category)}
          </span>
        </div>

        <h3 className="text-base font-semibold text-[#1a1a1a] mb-2 group-hover:text-[#C8714A] transition-colors">
          {displayName}
        </h3>

        <p className="text-sm text-[#888] leading-relaxed line-clamp-2 mb-4 flex-1">
          {displayDesc}
        </p>

        <div className="flex gap-3 mb-4 flex-wrap">
          {product.specs?.size && (
            <div className="flex items-center gap-1 text-xs text-[#aaa]">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-ruler-line text-xs"></i>
              </div>
              <span>{product.specs.size}</span>
            </div>
          )}
          {product.specs?.weight && (
            <div className="flex items-center gap-1 text-xs text-[#aaa]">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-scales-line text-xs"></i>
              </div>
              <span>{product.specs.weight}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#f5f0eb]">
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-[#C8714A]">${product.price}</span>
            {product.original_price > product.price && (
              <span className="text-sm text-[#ccc] line-through">${product.original_price}</span>
            )}
          </div>
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8714A]/10 group-hover:bg-[#C8714A] transition-colors duration-300">
            <i className="ri-arrow-right-line text-[#C8714A] group-hover:text-white text-sm transition-colors duration-300"></i>
          </div>
        </div>
      </div>
    </Link>
  );
}